import { useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, CalendarDays, ClipboardList, TrendingUp, Eye, Mail, Download, X, LayoutGrid, Clock, Plus, Trash2, Bell } from "lucide-react";

const KINDS = [
  { key: "daily",          label: "Daily Report",           icon: CalendarDays,  desc: "All parcels for a given day",                paramKey: "date",  paramLabel: "Date",  paramType: "date"  },
  { key: "mis",            label: "MIS Report",             icon: TrendingUp,    desc: "Monthly management summary",                 paramKey: "month", paramLabel: "Month", paramType: "month" },
  { key: "runsheet",       label: "Delivery Run Sheet",     icon: ClipboardList, desc: "Pending delivery list with signature blocks", paramKey: null,    paramLabel: "",      paramType: null    },
  { key: "reconciliation", label: "Reconciliation Summary", icon: LayoutGrid,    desc: "Aged buckets · inbound/outbound · per branch", paramKey: "date",  paramLabel: "As on",  paramType: "date"  },
];

export default function OperationalReportsSection({ branchId }) {
  const [preview, setPreview] = useState(null); // { kind, data, param }
  const [emailOpen, setEmailOpen] = useState(false);
  const [selected, setSelected] = useState({ daily: false, mis: false, runsheet: false, reconciliation: false });
  const [emailForm, setEmailForm] = useState({ to: "", note: "", format: "pdf", date: today(), month: thisMonth(), from_dt: "", to_dt: "" });
  const [params, setParams] = useState({ date: today(), month: thisMonth(), from_dt: "", to_dt: "" });
  const [busy, setBusy] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);

  const doPreview = async (kind) => {
    setBusy(true);
    try {
      const p = { branch_id: branchId || undefined };
      if (kind === "daily") p.date = params.date;
      if (kind === "reconciliation") p.date = params.date;
      if (kind === "mis")   p.month = params.month;
      if (params.from_dt) p.from_dt = params.from_dt;
      if (params.to_dt) p.to_dt = params.to_dt;
      const { data } = await api.get(`/reports/${kind}/preview`, { params: p });
      setPreview({ kind, data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doDownload = async (kind, format) => {
    try {
      const p = { format, branch_id: branchId || undefined };
      if (kind === "daily" || kind === "reconciliation") p.date = params.date;
      if (kind === "mis")   p.month = params.month;
      if (params.from_dt) p.from_dt = params.from_dt;
      if (params.to_dt) p.to_dt = params.to_dt;
      const { data } = await api.get(`/reports/document/${kind}/export`, { params: p, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${kind}-${p.date || p.month || "today"}.${format === "excel" ? "xlsx" : format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const openEmail = (kind) => {
    setSelected({ daily: kind === "daily", mis: kind === "mis", runsheet: kind === "runsheet", reconciliation: kind === "reconciliation" });
    setEmailForm(f => ({ ...f, date: params.date, month: params.month, from_dt: params.from_dt, to_dt: params.to_dt }));
    setEmailOpen(true);
  };
  const openEmailCombined = () => {
    setSelected({ daily: true, mis: true, runsheet: true, reconciliation: true });
    setEmailForm(f => ({ ...f, date: params.date, month: params.month, from_dt: params.from_dt, to_dt: params.to_dt }));
    setEmailOpen(true);
  };
  const sendEmail = async () => {
    const kinds = Object.keys(selected).filter(k => selected[k]);
    if (!kinds.length) return toast.error("Select at least one report");
    if (!emailForm.to) return toast.error("Recipient email required");
    setBusy(true);
    try {
      const { data } = await api.post("/reports/email", {
        to: emailForm.to, kinds, note: emailForm.note, format: emailForm.format,
        date: emailForm.date, month: emailForm.month,
        from_dt: emailForm.from_dt || undefined,
        to_dt: emailForm.to_dt || undefined,
        branch_id: branchId || undefined,
      });
      toast.success(data.ok ? `Sent to ${data.sent_to}` : "Queued (email service unavailable)");
      setEmailOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="border border-border bg-card rounded-md" data-testid="operational-reports">
      <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Operational documents</div>
          <div className="font-display text-xl mt-1">Reconciliation · Daily · MIS · Delivery Run Sheet</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setSchedulesOpen(true)} data-testid="schedules-btn" className="rounded-md">
            <Bell size={13} className="mr-1.5" /> Auto-email schedules
          </Button>
          <Button variant="outline" size="sm" onClick={openEmailCombined} data-testid="email-combined-btn" className="rounded-md">
            <Mail size={13} className="mr-1.5" /> Email selected together
          </Button>
        </div>
      </div>

      {/* Global date-time range · applies to all reports below */}
      <div className="px-5 pt-3 pb-1 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="report-range">
        <label className="block">
          <span className="overline flex items-center gap-1.5"><Clock size={11} /> From (date &amp; time)</span>
          <Input data-testid="range-from" type="datetime-local" value={params.from_dt}
            onChange={(e) => setParams(p => ({ ...p, from_dt: e.target.value }))}
            className="mt-1 rounded-md h-9 mono text-xs" />
        </label>
        <label className="block">
          <span className="overline flex items-center gap-1.5"><Clock size={11} /> To (date &amp; time)</span>
          <Input data-testid="range-to" type="datetime-local" value={params.to_dt}
            onChange={(e) => setParams(p => ({ ...p, to_dt: e.target.value }))}
            className="mt-1 rounded-md h-9 mono text-xs" />
        </label>
      </div>
      <div className="px-5 -mt-1 pb-1 text-[10px] text-muted-foreground">
        Leave both empty to use the per-report date/month picker below. Applies to Daily / MIS / Run Sheet.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-5">
        {KINDS.map(k => (
          <div key={k.key} className="border border-border rounded-md p-4 hover:border-primary transition-colors flex flex-col gap-2" data-testid={`report-card-${k.key}`}>
            <div className="flex items-center gap-2">
              <k.icon size={16} className="text-primary" />
              <div className="font-display text-lg">{k.label}</div>
            </div>
            <div className="text-xs text-muted-foreground flex-1">{k.desc}</div>
            {k.paramKey && (
              <Input
                data-testid={`report-${k.key}-param`}
                type={k.paramType}
                value={params[k.paramKey]}
                onChange={(e) => setParams(p => ({ ...p, [k.paramKey]: e.target.value }))}
                className="rounded-md text-xs mono h-8"
              />
            )}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button data-testid={`preview-${k.key}`} disabled={busy} onClick={() => doPreview(k.key)}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-border rounded px-2 py-1 hover:border-primary hover:text-primary transition-colors">
                <Eye size={11} /> Preview
              </button>
              <button data-testid={`download-${k.key}-pdf`} disabled={busy} onClick={() => doDownload(k.key, "pdf")}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-primary/40 text-primary bg-primary/5 rounded px-2 py-1 hover:bg-primary hover:text-white transition-colors">
                <Download size={11} /> PDF
              </button>
              <button data-testid={`download-${k.key}-xlsx`} disabled={busy} onClick={() => doDownload(k.key, "xlsx")}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-border rounded px-2 py-1 hover:border-primary hover:text-primary transition-colors">
                <FileText size={11} /> Excel
              </button>
              <button data-testid={`download-${k.key}-csv`} disabled={busy} onClick={() => doDownload(k.key, "csv")}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-border rounded px-2 py-1 hover:border-primary hover:text-primary transition-colors">
                <FileText size={11} /> CSV
              </button>
              <button data-testid={`email-${k.key}`} disabled={busy} onClick={() => openEmail(k.key)}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-border rounded px-2 py-1 hover:border-primary hover:text-primary transition-colors">
                <Mail size={11} /> Email
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview modal */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-md max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{preview?.data?.title}</DialogTitle>
            <div className="text-xs text-muted-foreground">{preview?.data?.subtitle}</div>
          </DialogHeader>
          {preview && (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                <Button size="sm" variant="outline" onClick={() => doDownload(preview.kind, "pdf")} className="rounded-md" data-testid="preview-download-pdf"><Download size={12} className="mr-1"/> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => doDownload(preview.kind, "xlsx")} className="rounded-md" data-testid="preview-download-xlsx"><FileText size={12} className="mr-1"/> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => doDownload(preview.kind, "csv")} className="rounded-md" data-testid="preview-download-csv"><FileText size={12} className="mr-1"/> CSV</Button>
                <Button size="sm" onClick={() => { setPreview(null); openEmail(preview.kind); }} className="rounded-md bg-primary text-primary-foreground" data-testid="preview-email"><Mail size={12} className="mr-1"/> Email this</Button>
              </div>
              <div className="overflow-x-auto border border-border rounded-md">
                <table className="w-full text-xs" data-testid="preview-table">
                  <thead className="bg-primary text-primary-foreground">
                    <tr>{preview.data.headers.map((h, i) => <th key={i} className="p-2 text-left">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.data.rows.map((r, i) => (
                      <tr key={i} className={`border-b border-border ${i % 2 === 0 ? "bg-muted/40" : ""}`}>
                        {r.map((c, j) => <td key={j} className="p-2 mono">{String(c)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email modal */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-md max-w-md" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Mail size={16} className="text-primary"/> Email reports</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="overline mb-2">Include</div>
              <div className="grid grid-cols-3 gap-2">
                {KINDS.map(k => (
                  <label key={k.key} className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer text-xs ${selected[k.key] ? "border-primary bg-red-50" : "border-border"}`} data-testid={`email-select-${k.key}`}>
                    <input type="checkbox" checked={!!selected[k.key]} onChange={e => setSelected(s => ({ ...s, [k.key]: e.target.checked }))} className="accent-[#BE1E1E]"/>
                    <span className="font-semibold">{k.label.replace(" Report","").replace(" Summary","")}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="overline">Send to</span>
              <Input data-testid="email-to" type="email" required value={emailForm.to} onChange={e => setEmailForm(f => ({ ...f, to: e.target.value }))} className="mt-1 rounded-md" placeholder="admin@company.com" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="overline">Date (Daily / Reconciliation)</span>
                <Input data-testid="email-date" type="date" value={emailForm.date} onChange={e => setEmailForm(f => ({ ...f, date: e.target.value }))} className="mt-1 rounded-md" />
              </label>
              <label className="block">
                <span className="overline">Month (MIS)</span>
                <Input data-testid="email-month" type="month" value={emailForm.month} onChange={e => setEmailForm(f => ({ ...f, month: e.target.value }))} className="mt-1 rounded-md" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="overline">From (time range · optional)</span>
                <Input data-testid="email-from-dt" type="datetime-local" value={emailForm.from_dt} onChange={e => setEmailForm(f => ({ ...f, from_dt: e.target.value }))} className="mt-1 rounded-md" />
              </label>
              <label className="block">
                <span className="overline">To (time range · optional)</span>
                <Input data-testid="email-to-dt" type="datetime-local" value={emailForm.to_dt} onChange={e => setEmailForm(f => ({ ...f, to_dt: e.target.value }))} className="mt-1 rounded-md" />
              </label>
            </div>
            <label className="block">
              <span className="overline">Format</span>
              <div className="inline-flex mt-1 border border-border rounded-md overflow-hidden text-xs">
                {["pdf","xlsx","csv"].map(f => (
                  <button key={f} data-testid={`email-fmt-${f}`} type="button" onClick={() => setEmailForm(x => ({ ...x, format: f }))}
                    className={`px-3 py-1.5 uppercase tracking-widest font-semibold ${emailForm.format === f ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{f}</button>
                ))}
              </div>
            </label>
            <label className="block">
              <span className="overline">Note (optional)</span>
              <Textarea data-testid="email-note" value={emailForm.note} onChange={e => setEmailForm(f => ({ ...f, note: e.target.value }))} rows={2} className="mt-1 rounded-md"/>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-md" data-testid="email-cancel"><X size={13} className="mr-1"/> Cancel</Button>
              <Button onClick={sendEmail} disabled={busy} className="rounded-md bg-primary text-primary-foreground" data-testid="email-send">
                <Mail size={13} className="mr-1"/> {busy ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SchedulesDialog open={schedulesOpen} onOpenChange={setSchedulesOpen} branchId={branchId} />
    </section>
  );
}

function SchedulesDialog({ open, onOpenChange, branchId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    kinds: ["reconciliation"], recipients: "", frequency: "daily",
    hour_utc: 3, direction: "", format: "pdf", note: "", active: true,
  });

  const load = async () => {
    try { const { data } = await api.get("/reports/schedules"); setItems(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load schedules"); }
  };

  useState(() => { /* noop for first paint */ });
  if (open && items.length === 0 && !busy) load();

  const create = async () => {
    const recips = (form.recipients || "").split(/[,\s]+/).filter(r => r.includes("@"));
    if (!recips.length) return toast.error("Add at least one recipient email");
    if (!form.kinds.length) return toast.error("Pick at least one report kind");
    setBusy(true);
    try {
      await api.post("/reports/schedules", {
        kinds: form.kinds, recipients: recips, frequency: form.frequency,
        hour_utc: Number(form.hour_utc) || 3,
        direction: form.direction || undefined,
        branch_id: branchId || undefined,
        format: form.format, note: form.note, active: form.active,
      });
      toast.success("Schedule created");
      setForm(f => ({ ...f, recipients: "", note: "" }));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (s) => {
    try { await api.patch(`/reports/schedules/${s.id}`, { ...s, active: !s.active }); await load(); }
    catch (e) { toast.error("Failed to toggle"); }
  };
  const remove = async (s) => {
    if (!window.confirm("Delete this schedule?")) return;
    try { await api.delete(`/reports/schedules/${s.id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="schedules-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Bell size={18} className="text-primary" /> Auto-email schedules</DialogTitle>
          <div className="text-xs text-muted-foreground">Reconciliation, Daily, MIS, and Run Sheet reports auto-emailed at the configured hour (UTC) to recipients.</div>
        </DialogHeader>

        <div className="border border-primary/40 rounded-md p-4 bg-red-50/40 space-y-3">
          <div className="overline text-primary flex items-center gap-2"><Plus size={12} /> New schedule</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="overline">Reports to include</span>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {KINDS.map(k => (
                  <label key={k.key} className={`flex items-center gap-2 border rounded p-1.5 cursor-pointer text-xs ${form.kinds.includes(k.key) ? "border-primary bg-red-50" : "border-border"}`}
                    data-testid={`sched-kind-${k.key}`}>
                    <input type="checkbox" checked={form.kinds.includes(k.key)}
                      onChange={(e) => setForm(f => ({ ...f, kinds: e.target.checked ? [...f.kinds, k.key] : f.kinds.filter(x => x !== k.key) }))}
                      className="accent-[#BE1E1E]" />
                    <span className="font-semibold">{k.label.replace(" Report","").replace(" Summary","")}</span>
                  </label>
                ))}
              </div>
            </label>
            <label className="block">
              <span className="overline">Recipients (comma / space separated)</span>
              <Textarea data-testid="sched-recipients" rows={2}
                value={form.recipients} onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))}
                placeholder="ops@company.com, admin@company.com" className="mt-1 rounded-md" />
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="block">
              <span className="overline">Frequency</span>
              <select data-testid="sched-frequency" value={form.frequency}
                onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>
            <label className="block">
              <span className="overline">Hour UTC (0-23)</span>
              <Input data-testid="sched-hour" type="number" min="0" max="23" value={form.hour_utc}
                onChange={e => setForm(f => ({ ...f, hour_utc: e.target.value }))}
                className="mt-1 rounded-md font-mono" />
            </label>
            <label className="block">
              <span className="overline">Direction (optional)</span>
              <select data-testid="sched-direction" value={form.direction}
                onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="">Both</option>
                <option value="inward">Inward only</option>
                <option value="outward">Outward only</option>
              </select>
            </label>
            <label className="block">
              <span className="overline">Format</span>
              <select data-testid="sched-format" value={form.format}
                onChange={e => setForm(f => ({ ...f, format: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="pdf">PDF</option>
                <option value="xlsx">Excel</option>
                <option value="csv">CSV</option>
              </select>
            </label>
          </div>
          <div className="flex justify-end">
            <Button onClick={create} disabled={busy} data-testid="sched-create"
              className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
              <Plus size={13} className="mr-1.5" /> {busy ? "Saving…" : "Create schedule"}
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <div className="overline mb-2">Active schedules</div>
          {items.length === 0 && <div className="text-xs text-muted-foreground p-3 border border-dashed border-border rounded-md">No schedules yet.</div>}
          <div className="space-y-2">
            {items.map(s => (
              <div key={s.id} data-testid={`sched-item-${s.id}`} className="border border-border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm flex-1 min-w-[240px]">
                  <div className="font-semibold">{(s.kinds || []).join(" · ")}</div>
                  <div className="text-[11px] text-muted-foreground">
                    to {(s.recipients || []).join(", ")} · {s.frequency} @ {String(s.hour_utc).padStart(2,"0")}:00 UTC
                    {s.direction ? ` · ${s.direction}` : ""} · {s.format?.toUpperCase()}
                  </div>
                  <div className="text-[10px] mono text-muted-foreground mt-1">
                    Next: {s.next_run_at?.slice(0,16).replace("T"," ") || "—"} · Last: {s.last_run_at?.slice(0,16).replace("T"," ") || "—"} ({s.last_run_status || "—"})
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`sched-toggle-${s.id}`} onClick={() => toggle(s)}
                    className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border ${s.active ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-border text-muted-foreground"}`}>
                    {s.active ? "Active" : "Paused"}
                  </button>
                  <button data-testid={`sched-delete-${s.id}`} onClick={() => remove(s)}
                    className="text-[10px] font-semibold px-2 py-1 rounded border border-primary/50 text-primary hover:bg-primary hover:text-white">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function thisMonth() { return new Date().toISOString().slice(0, 7); }
